import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import TutorialHint from '../components/TutorialHint';
import BibleBooksDialog from '../components/BibleBooksDialog';
import { OT_BOOK_VIDEOS, NT_BOOK_VIDEOS } from '../data/bibleBookVideos';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { BookOpen, ExternalLink, Volume2, Compass, Library, FileText, ZoomIn, Search, Loader2, Heart, Home, Youtube, Wind, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AlphaVideoModal } from '../components/AlphaVideoModal';
import ParentResources from './ParentResources';

const Resources = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { API } = useContext(AuthContext);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [bibleSubTab, setBibleSubTab] = useState('audio'); // For Bible Resources sub-tabs
  const [additionalSubTab, setAdditionalSubTab] = useState('alpha'); // For Additional Resources sub-tabs
  const [otBooksOpen, setOtBooksOpen] = useState(false);
  const [ntBooksOpen, setNtBooksOpen] = useState(false);
  const [booksDialogInitialSearch, setBooksDialogInitialSearch] = useState('');

  // Read ?tab= param from URL to control the active main tab
  const params = new URLSearchParams(location.search);
  const tabFromUrl = params.get('tab');
  const validTabs = ['prayer', 'bible', 'holy-spirit', 'additional', 'parents'];
  // Backward-compat: legacy ?tab=devotionals now maps to holy-spirit
  const normalizedTab = tabFromUrl === 'devotionals' ? 'holy-spirit' : tabFromUrl;
  const initialTab = validTabs.includes(normalizedTab) ? normalizedTab : 'prayer';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Sync activeTab when URL changes (e.g., navigating from Discipleship Pathway link)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const t = p.get('tab');
    const n = t === 'devotionals' ? 'holy-spirit' : t;
    if (validTabs.includes(n)) {
      setActiveTab(n);
    }
  }, [location.search]);

  // Auto-open the OT/NT books dialog when deep-linked from Global Search
  // (e.g. /resources?tab=bible&openBooks=ot&book=Genesis)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const openBooks = p.get('openBooks');
    const book = p.get('book') || '';
    if (openBooks === 'ot') {
      setActiveTab('bible');
      setBibleSubTab('guides');
      setBooksDialogInitialSearch(book);
      setOtBooksOpen(true);
    } else if (openBooks === 'nt') {
      setActiveTab('bible');
      setBibleSubTab('guides');
      setBooksDialogInitialSearch(book);
      setNtBooksOpen(true);
    }
  }, [location.search]);

  // Scroll to the section indicated by the URL hash (e.g., #exploring-christianity)
  // Wait for the tab content to render before scrolling.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    const tryScroll = (attempts = 0) => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts < 10) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      }
    };
    // Slight delay to allow tab content to mount
    setTimeout(() => tryScroll(), 150);
  }, [location.hash, activeTab]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await axios.post(`${API}/bible/search`, { topic: searchTerm.trim() });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults({
        topic: searchTerm,
        summary: "Unable to search at this time. Please try the external links below.",
        verses: [],
        related_topics: [],
        error: true
      });
    } finally {
      setIsSearching(false);
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-heading font-bold text-[#2B6B7F]">
              Resources
            </h1>
            <TutorialHint videoId="2oMR3vcn9dA" />
          </div>
          <p className="text-slate-600">Comprehensive tools for studying God&apos;s Word</p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-auto gap-1">
            <TabsTrigger value="prayer" className="flex flex-col items-center gap-1 py-3 px-1 h-auto text-center" data-testid="prayer-tab">
              <Heart className="w-5 h-5 flex-shrink-0 text-rose-500" />
              <span className="text-xs leading-tight">Personal<br/>Prayer</span>
            </TabsTrigger>
            <TabsTrigger value="bible" className="flex flex-col items-center gap-1 py-3 px-1 h-auto text-center" data-testid="bible-resources-tab">
              <BookOpen className="w-5 h-5 flex-shrink-0 text-blue-600" />
              <span className="text-xs leading-tight">Bible<br/>Resources</span>
            </TabsTrigger>
            <TabsTrigger value="holy-spirit" className="flex flex-col items-center gap-1 py-3 px-1 h-auto text-center" data-testid="holy-spirit-tab">
              <Wind className="w-5 h-5 flex-shrink-0 text-sky-500" />
              <span className="text-xs leading-tight">Holy<br/>Spirit</span>
            </TabsTrigger>
            <TabsTrigger value="additional" className="flex flex-col items-center gap-1 py-3 px-1 h-auto text-center">
              <FileText className="w-5 h-5 flex-shrink-0 text-teal-600" />
              <span className="text-xs leading-tight">Additional<br/>Resources</span>
            </TabsTrigger>
            <TabsTrigger value="parents" className="flex flex-col items-center gap-1 py-3 px-1 h-auto text-center" data-testid="parents-tab">
              <Home className="w-5 h-5 flex-shrink-0 text-pink-500" />
              <span className="text-xs leading-tight">For<br/>Parents</span>
            </TabsTrigger>
          </TabsList>

          {/* Holy Spirit Tab */}
          <TabsContent value="holy-spirit">
            <Card className="border-t-4 border-t-sky-500" data-testid="holy-spirit-tab-content">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wind className="w-6 h-6 text-sky-600" />
                  Holy Spirit
                </CardTitle>
                <CardDescription>
                  Trusted teaching on the person, gospel, and indwelling of the Holy Spirit.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <a
                  href="https://my.seedbed.com/courses/the-gospel-of-the-holy-spirit/?utm_source=chatgpt.com"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block p-4 rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50 to-white hover:from-sky-100 hover:border-sky-400 transition-colors"
                  data-testid="holy-spirit-seedbed-link"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-sky-600 font-semibold mb-1">
                        Seedbed Course
                      </p>
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">
                        The Gospel of the Holy Spirit
                      </h3>
                      <p className="text-sm text-slate-600">
                        A rich, Wesleyan course exploring who the Holy Spirit is and how the Spirit
                        works in the life of every believer.
                      </p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-sky-500 flex-shrink-0 mt-1" />
                  </div>
                </a>

                <a
                  href="https://www.holyspiritministrycenter.com/class/v/indwelling-baptism-and-filling-of-the-spirit?utm_source=chatgpt.com"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block p-4 rounded-lg border border-sky-200 bg-gradient-to-br from-sky-50 to-white hover:from-sky-100 hover:border-sky-400 transition-colors"
                  data-testid="holy-spirit-hsmc-link"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wider text-sky-600 font-semibold mb-1">
                        Holy Spirit Ministry Center
                      </p>
                      <h3 className="text-lg font-semibold text-slate-800 mb-1">
                        Indwelling, Baptism, and Filling of the Spirit
                      </h3>
                      <p className="text-sm text-slate-600">
                        A focused class distinguishing the indwelling, baptism, and filling
                        of the Holy Spirit — foundational teaching for every disciple.
                      </p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-sky-500 flex-shrink-0 mt-1" />
                  </div>
                </a>

                <div className="text-xs text-slate-500 text-center pt-2">
                  Links open in a new tab. External sites — content is provided by Seedbed and the
                  Holy Spirit Ministry Center.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Personal Prayer Tab */}
          <TabsContent value="prayer">
            <Card className="border-t-4 border-t-rose-500">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
                      <Heart className="w-6 h-6 text-rose-500" />
                      Pray As You Go
                    </CardTitle>
                    <CardDescription>
                      Daily prayer sessions with music, scripture, and guided reflections from the Jesuits
                    </CardDescription>
                  </div>
                  <a
                    href="https://prayasyougo.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors text-sm"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open in New Tab
                  </a>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Embedded Prayer Site */}
                  <div className="relative w-full rounded-lg overflow-hidden border border-rose-200 bg-white" style={{ height: '650px' }}>
                    <iframe
                      src="https://prayasyougo.org"
                      title="Pray As You Go - Daily Prayer"
                      className="w-full h-full"
                      style={{ border: 'none' }}
                      allow="autoplay; encrypted-media"
                      allowFullScreen
                    />
                  </div>
                  
                  {/* Info about the resource */}
                  <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-sm text-rose-800">
                      <strong>About Pray As You Go:</strong> A daily prayer session designed to help you pray whenever and wherever you find yourself. 
                      Each session includes music, scripture, and guided reflections based on Ignatian spirituality. 
                      Sessions are typically 10-13 minutes - perfect for commutes, walks, or quiet moments.
                    </p>
                  </div>

                  {/* App download links */}
                  <div className="flex flex-wrap gap-3 justify-center">
                    <a
                      href="https://apps.apple.com/app/pray-as-you-go/id369988789"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-900 transition-colors text-sm"
                    >
                      Download on App Store
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=com.prayasyougo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      Get it on Google Play
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bible Resources Tab - Contains 3 sub-sections */}
          <TabsContent value="bible">
            <Card className="border-t-4 border-t-blue-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                  Bible Resources
                </CardTitle>
                <CardDescription>
                  Audio Bible, video guides, and study tools for deeper understanding
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Sub-tabs for Bible Resources */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2 border-b pb-4">
                    <button
                      onClick={() => setBibleSubTab('audio')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        bibleSubTab === 'audio' 
                          ? 'bg-green-100 text-green-800 border-2 border-green-400' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Volume2 className="w-4 h-4" />
                      Audio Bible
                    </button>
                    <button
                      onClick={() => setBibleSubTab('guides')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        bibleSubTab === 'guides' 
                          ? 'bg-purple-100 text-purple-800 border-2 border-purple-400' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Compass className="w-4 h-4" />
                      Interactive Guides
                    </button>
                    <button
                      onClick={() => setBibleSubTab('study')}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        bibleSubTab === 'study' 
                          ? 'bg-indigo-100 text-indigo-800 border-2 border-indigo-400' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      <Library className="w-4 h-4" />
                      Study Resources
                    </button>
                  </div>
                </div>

                {/* Audio Bible Sub-section */}
                {bibleSubTab === 'audio' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Volume2 className="w-5 h-5 text-green-600" />
                      <h3 className="text-lg font-semibold text-slate-800">Audio Bible</h3>
                      <span className="text-sm text-slate-500">- Listen to the Bible read aloud with dramatic audio</span>
                    </div>
                    <iframe 
                      src="https://live.bible.is/bible/engesv/mat/1?audio_type=audio_drama"
                      title="Audio Bible"
                      className="w-full border-0 rounded-lg h-[85vh] min-h-[560px] max-h-[900px]"
                      allow="autoplay"
                    />
                  </div>
                )}

                {/* Interactive Guides Sub-section */}
                {bibleSubTab === 'guides' && (
                  <div className="space-y-6">
                    {/* Old Testament Guide */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-purple-50 border-b border-purple-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Compass className="w-5 h-5 text-purple-600" />
                            <h3 className="font-semibold text-purple-800">📖 Old Testament Overview</h3>
                          </div>
                          <a
                            href="https://bibleproject.com/guides/categories/old-testament/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                          >
                            Full Guide <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Watch animated overviews of every Old Testament book from The Bible Project</p>
                      </div>
                      <div className="aspect-video w-full">
                        <iframe
                          src="https://www.youtube.com/embed/videoseries?list=PLH0Szn1yYNeeVFodkI9J_WEATHQCwRZ0u"
                          title="BibleProject Old Testament Overview"
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="p-3 border-t border-purple-200 bg-purple-50/50">
                        <button
                          onClick={() => setOtBooksOpen(true)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                          data-testid="open-ot-books-dialog"
                        >
                          <BookOpen className="w-4 h-4" />
                          Explore All 41 Old Testament Book Videos
                        </button>
                      </div>
                    </div>

                    {/* New Testament Guide */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-indigo-50 border-b border-indigo-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Compass className="w-5 h-5 text-indigo-600" />
                            <h3 className="font-semibold text-indigo-800">📖 New Testament Overview</h3>
                          </div>
                          <a
                            href="https://bibleproject.com/guides/categories/new-testament/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          >
                            Full Guide <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Watch animated overviews of every New Testament book from The Bible Project</p>
                      </div>
                      <div className="aspect-video w-full">
                        <iframe
                          src="https://www.youtube.com/embed/videoseries?list=PLH0Szn1yYNecanpQqdixWAm3zHdhY2kPR"
                          title="BibleProject New Testament Overview"
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="p-3 border-t border-indigo-200 bg-indigo-50/50">
                        <button
                          onClick={() => setNtBooksOpen(true)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                          data-testid="open-nt-books-dialog"
                        >
                          <BookOpen className="w-4 h-4" />
                          Explore All 32 New Testament Book Videos
                        </button>
                      </div>
                    </div>

                    {/* Rose Handbook PDF */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-rose-50 border-b border-rose-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Compass className="w-5 h-5 text-rose-600" />
                            <h3 className="font-semibold text-rose-800">📚 Rose Handbook of Charts, Maps, and Timelines</h3>
                          </div>
                          <a
                            href="https://files.tyndale.com/thpdata/firstchapters/978-1-4964-8485-7.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-rose-600 hover:text-rose-800 flex items-center gap-1"
                          >
                            Open PDF <ExternalLink className="w-4 h-4" />
                          </a>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Visual aids for understanding the Bible</p>
                      </div>
                      <div className="w-full bg-slate-100">
                        <iframe
                          src="https://docs.google.com/viewer?url=https%3A%2F%2Ffiles.tyndale.com%2Fthpdata%2Ffirstchapters%2F978-1-4964-8485-7.pdf&embedded=true"
                          title="Rose Handbook PDF"
                          className="w-full border-0"
                          style={{ height: '500px' }}
                          data-testid="rose-handbook-pdf-iframe"
                        />
                        <div className="flex items-center justify-between p-3 bg-white border-t border-slate-200 text-sm">
                          <span className="text-slate-600 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Can&apos;t see the PDF above?
                          </span>
                          <a
                            href="https://files.tyndale.com/thpdata/firstchapters/978-1-4964-8485-7.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-1.5 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors"
                            data-testid="rose-handbook-pdf-open-new-tab"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open in New Tab
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Bible Timeline */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-amber-50 border-b border-amber-200">
                        <div className="flex items-center gap-2">
                          <Compass className="w-5 h-5 text-amber-600" />
                          <h3 className="font-semibold text-amber-800">🗓️ Bible Timeline</h3>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Visual overview of Bible history</p>
                      </div>
                      <div className="p-4">
                        <Dialog open={timelineOpen} onOpenChange={setTimelineOpen}>
                          <DialogTrigger asChild>
                            <button className="group block w-full p-4 border-2 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all text-left">
                              <div className="flex items-center gap-4">
                                <div className="w-24 h-16 rounded overflow-hidden flex-shrink-0 border border-slate-200">
                                  <img 
                                    src="https://customer-assets.emergentagent.com/job_2ef9aa86-2489-4e4a-84b7-ecd5bb839f78/artifacts/fzjs5i2u_visual-overview-of-the-bible-hobo-theology-scaled.jpg"
                                    alt="Bible Timeline Preview"
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-lg font-semibold text-slate-800 group-hover:text-amber-700 mb-1">
                                    Visual Overview of the Bible
                                  </h3>
                                  <p className="text-sm text-slate-600">
                                    See the complete timeline of biblical history from Genesis to Revelation
                                  </p>
                                </div>
                                <ZoomIn className="w-6 h-6 text-slate-400 group-hover:text-amber-600 flex-shrink-0" />
                              </div>
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 overflow-hidden">
                            <DialogHeader className="px-6 pt-6 pb-4 border-b">
                              <DialogTitle className="text-xl text-[#2B6B7F] flex items-center gap-2">
                                <Compass className="w-5 h-5 text-amber-600" />
                                Visual Overview of the Bible
                              </DialogTitle>
                            </DialogHeader>
                            <div className="overflow-auto p-4" style={{ maxHeight: 'calc(95vh - 100px)' }}>
                              <img 
                                src="https://customer-assets.emergentagent.com/job_2ef9aa86-2489-4e4a-84b7-ecd5bb839f78/artifacts/fzjs5i2u_visual-overview-of-the-bible-hobo-theology-scaled.jpg"
                                alt="Visual Overview of the Bible Timeline"
                                className="w-full h-auto rounded-lg"
                              />
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                )}

                {/* Study Resources Sub-section */}
                {bibleSubTab === 'study' && (
                  <div className="space-y-6">
                    {/* Bible Word Search */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-indigo-50 border-b border-indigo-200">
                        <div className="flex items-center gap-2">
                          <Search className="w-5 h-5 text-indigo-600" />
                          <h3 className="font-semibold text-indigo-800">Bible Word Search</h3>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Search for any word or topic in the Bible</p>
                      </div>
                      <div className="p-4 space-y-4">
                        {/* Search Input */}
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              type="text"
                              placeholder="Enter a word or topic (e.g., love, faith, grace, hope)"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && searchTerm.trim()) {
                                  handleSearch();
                                }
                              }}
                              className="pl-10"
                            />
                          </div>
                          <Button 
                            onClick={handleSearch}
                            disabled={!searchTerm.trim() || isSearching}
                            className="bg-indigo-600 hover:bg-indigo-700"
                          >
                            {isSearching ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Search className="w-4 h-4 mr-2" />
                            )}
                            Search
                          </Button>
                        </div>

                        {/* Loading State */}
                        {isSearching && (
                          <div className="text-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-3" />
                            <p className="text-slate-600">Searching the Bible for &quot;{searchTerm}&quot;...</p>
                          </div>
                        )}

                        {/* Search Results */}
                        {searchResults && !isSearching && (
                          <div className="space-y-4 pt-4 border-t animate-fade-in">
                            <div className="p-4 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
                              <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                                <BookOpen className="w-5 h-5" />
                                What the Bible Says About &quot;{searchResults.topic}&quot;
                              </h4>
                              <p className="text-slate-700">{searchResults.summary}</p>
                            </div>

                            {searchResults.verses && searchResults.verses.length > 0 && (
                              <div className="space-y-3">
                                <h4 className="font-semibold text-slate-800">Key Verses:</h4>
                                {searchResults.verses.map((verse, idx) => (
                                  <div key={idx} className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                                    <div className="flex items-start gap-3">
                                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                        <span className="text-indigo-700 font-bold text-sm">{idx + 1}</span>
                                      </div>
                                      <div className="flex-1">
                                        <h5 className="font-semibold text-indigo-700 mb-1">{verse.reference}</h5>
                                        <p className="text-slate-700 italic mb-2">&quot;{verse.text}&quot;</p>
                                        {verse.explanation && (
                                          <p className="text-sm text-slate-600 bg-slate-50 p-2 rounded">{verse.explanation}</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {searchResults.related_topics && searchResults.related_topics.length > 0 && (
                              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <h5 className="font-semibold text-amber-800 mb-2">Related Topics:</h5>
                                <div className="flex flex-wrap gap-2">
                                  {searchResults.related_topics.map((topic, idx) => (
                                    <button
                                      key={idx}
                                      onClick={() => {
                                        setSearchTerm(topic);
                                        setTimeout(() => handleSearch(), 100);
                                      }}
                                      className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition-colors"
                                    >
                                      {topic}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Popular Topics */}
                        {!searchResults && !isSearching && (
                          <div className="pt-2">
                            <p className="text-sm text-slate-500 mb-3">Popular topics to explore:</p>
                            <div className="flex flex-wrap gap-2">
                              {['Love', 'Faith', 'Grace', 'Hope', 'Peace', 'Joy', 'Salvation', 'Prayer', 'Forgiveness', 'Holy Spirit'].map((topic) => (
                                <button
                                  key={topic}
                                  onClick={() => {
                                    setSearchTerm(topic);
                                    setTimeout(() => handleSearch(), 100);
                                  }}
                                  className="px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors"
                                >
                                  {topic}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Commentary Resources */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="p-4 bg-blue-50 border-b border-blue-200">
                        <div className="flex items-center gap-2">
                          <Library className="w-5 h-5 text-blue-600" />
                          <h3 className="font-semibold text-blue-800">Bible Study Resources</h3>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Commentaries and study tools for deeper understanding</p>
                      </div>
                      <div className="p-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <a
                            href="https://biblehub.com/commentaries/icc/matthew/1.htm"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-4 border rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">Bible Hub Commentaries</h3>
                              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                            </div>
                            <p className="text-sm text-slate-600">International Critical Commentary and more</p>
                          </a>

                          <a
                            href="https://www.biblegateway.com/resources/ivp-nt/toc/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-4 border rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">IVP NT Commentary</h3>
                              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                            </div>
                            <p className="text-sm text-slate-600">In-depth New Testament study resources</p>
                          </a>

                          <a
                            href="https://www.biblegateway.com/resources/asbury-bible-commentary/toc/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-4 border rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">Asbury Bible Commentary</h3>
                              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                            </div>
                            <p className="text-sm text-slate-600">Comprehensive verse-by-verse commentary</p>
                          </a>

                          <a
                            href="https://www.studylight.org/commentaries/eng.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group p-4 border rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-semibold text-slate-800 group-hover:text-blue-700">StudyLight Commentaries</h3>
                              <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
                            </div>
                            <p className="text-sm text-slate-600">Multiple commentary collections in one place</p>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Resources Tab */}
          <TabsContent value="additional">
            <Card className="border-t-4 border-t-teal-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-6 h-6 text-teal-600" />
                  Additional Resources
                </CardTitle>
                <CardDescription>Explore Christianity and your discipleship journey</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Sub-tabs for Additional Resources — same pill style as Bible Resources above */}
                <div className="flex flex-wrap gap-2 border-b pb-4">
                  <button
                    onClick={() => setAdditionalSubTab('alpha')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      additionalSubTab === 'alpha'
                        ? 'bg-red-100 text-red-800 border-2 border-red-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="additional-tab-alpha"
                  >
                    <Youtube className="w-4 h-4" />
                    Alpha
                  </button>
                  <button
                    onClick={() => setAdditionalSubTab('discipleship')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      additionalSubTab === 'discipleship'
                        ? 'bg-teal-100 text-teal-800 border-2 border-teal-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="additional-tab-discipleship"
                  >
                    <FileText className="w-4 h-4" />
                    Discipleship Plan &amp; Pathway
                  </button>
                  <button
                    onClick={() => setAdditionalSubTab('seminary')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      additionalSubTab === 'seminary'
                        ? 'bg-amber-100 text-amber-800 border-2 border-amber-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="additional-tab-seminary"
                  >
                    <Youtube className="w-4 h-4" />
                    7 Minute Seminary
                  </button>
                  <button
                    onClick={() => setAdditionalSubTab('history')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      additionalSubTab === 'history'
                        ? 'bg-indigo-100 text-indigo-800 border-2 border-indigo-400'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="additional-tab-history"
                  >
                    <Landmark className="w-4 h-4" />
                    Bible History
                  </button>
                </div>

                {/* Alpha Sub-section */}
                {additionalSubTab === 'alpha' && (
                <div id="exploring-christianity" className="scroll-mt-20">
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-200">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h3 className="text-xl font-bold text-red-800 flex items-center gap-2">
                            🔥 Exploring Christianity - Alpha Film Series
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            A 14-session video series exploring the basics of the Christian faith. Perfect for seekers, new believers, or anyone wanting to deepen their understanding.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <AlphaVideoModal
                            buttonLabel="Start at Episode 1"
                            size="sm"
                            testId="resources-start-alpha"
                          />
                          <a
                            href="https://www.youtube.com/playlist?list=PLA7xqrtB-HHrET4NrEPUmVhVorhQpKGRr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View Full Playlist
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    {/* Embedded Playlist */}
                    <div className="aspect-video w-full bg-black">
                      <iframe
                        src="https://www.youtube.com/embed/videoseries?list=PLA7xqrtB-HHrET4NrEPUmVhVorhQpKGRr"
                        title="Alpha Film Series"
                        className="w-full h-full border-0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                    
                    {/* Video Descriptions */}
                    <div className="p-4 bg-white">
                      <h4 className="font-semibold text-slate-800 mb-4">Episode Guide:</h4>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 1:</span> Is There More To Life Than This?
                          <p className="text-xs text-slate-500 mt-1">Explores life's big questions and the search for meaning</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 2:</span> Who Is Jesus?
                          <p className="text-xs text-slate-500 mt-1">Examines the historical evidence for Jesus Christ</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 3:</span> Why Did Jesus Die?
                          <p className="text-xs text-slate-500 mt-1">Explores the meaning and significance of the cross</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 4:</span> How Can I Have Faith?
                          <p className="text-xs text-slate-500 mt-1">Discusses what faith is and how to develop it</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 5:</span> Why and How Should I Pray?
                          <p className="text-xs text-slate-500 mt-1">Practical guidance on developing a prayer life</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 6:</span> How and Why Should I Read the Bible?
                          <p className="text-xs text-slate-500 mt-1">Tips for engaging with Scripture meaningfully</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 7:</span> How Does God Guide Us?
                          <p className="text-xs text-slate-500 mt-1">Understanding God's guidance in our daily lives</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 8:</span> Introduction To The Weekend
                          <p className="text-xs text-slate-500 mt-1">Prepares for deeper exploration of the Holy Spirit</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 9:</span> Who Is The Holy Spirit?
                          <p className="text-xs text-slate-500 mt-1">Introduces the person and work of the Holy Spirit</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 10:</span> What Does the Holy Spirit Do?
                          <p className="text-xs text-slate-500 mt-1">Explores the Spirit's role in believers' lives</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 11:</span> How Can I Be Filled with the Holy Spirit?
                          <p className="text-xs text-slate-500 mt-1">Practical steps for receiving the Spirit's fullness</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 12:</span> How Can I Make The Most Of The Rest of My Life?
                          <p className="text-xs text-slate-500 mt-1">Living with purpose and eternal perspective</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 13:</span> How Can I Resist Evil?
                          <p className="text-xs text-slate-500 mt-1">Spiritual warfare and standing firm in faith</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg border">
                          <span className="text-red-600 font-bold">Ep 14:</span> Why and How Should I Tell Others?
                          <p className="text-xs text-slate-500 mt-1">Sharing your faith with confidence and love</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* Discipleship Plan & Pathway Sub-section */}
                {additionalSubTab === 'discipleship' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-teal-800">📘 Discipleship Plan & Pathway Guide</h3>
                      <p className="text-sm text-slate-600">A comprehensive guide for your discipleship journey</p>
                    </div>
                    <a
                      href="https://customer-assets-lqy194kg.emergentagent.net/job_c6c45f8d-756d-427c-8ad6-2091e406baff/artifacts/jw1u4cyk_Discipleship%20plan%20and%20pathway.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-teal-600 hover:text-teal-800 flex items-center gap-1"
                    >
                      Download PDF <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="w-full rounded-lg overflow-hidden border border-slate-200 bg-slate-100">
                    {/* Google Docs Viewer iframe — renders reliably on desktop AND mobile,
                        where a raw <object> or direct PDF iframe often shows a blank frame. */}
                    <iframe
                      src="https://docs.google.com/viewer?url=https%3A%2F%2Fcustomer-assets-lqy194kg.emergentagent.net%2Fjob_c6c45f8d-756d-427c-8ad6-2091e406baff%2Fartifacts%2Fjw1u4cyk_Discipleship%2520plan%2520and%2520pathway.pdf&embedded=true"
                      title="Discipleship Plan and Pathway PDF"
                      className="w-full border-0"
                      style={{ height: '600px' }}
                      data-testid="discipleship-pdf-iframe"
                    />
                    <div className="flex items-center justify-between p-3 bg-white border-t border-slate-200 text-sm">
                      <span className="text-slate-600 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Can&apos;t see the PDF above?
                      </span>
                      <a
                        href="https://customer-assets-lqy194kg.emergentagent.net/job_c6c45f8d-756d-427c-8ad6-2091e406baff/artifacts/jw1u4cyk_Discipleship%20plan%20and%20pathway.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                        data-testid="discipleship-pdf-open-new-tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open in New Tab
                      </a>
                    </div>
                  </div>
                </div>
                )}

                {/* 7 Minute Seminary Resources Sub-section */}
                {additionalSubTab === 'seminary' && (
                <div>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-200">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div>
                          <h3 className="text-xl font-bold text-amber-900 flex items-center gap-2">
                            📚 7 Minute Seminary
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            Bite-sized theology videos from Seedbed featuring trusted Wesleyan-leaning scholars. Perfect for quick, deep learning on Christian faith and Scripture.
                          </p>
                        </div>
                        <a
                          href="https://www.youtube.com/playlist?list=PLXPMuA-19by_hljikeTHj0mnpNTum-62X"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm"
                          data-testid="seminary-playlist-link"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Full Playlist
                        </a>
                      </div>
                    </div>

                    {/* Featured videos grid */}
                    <div className="p-4 bg-white space-y-6">
                      {[
                        {
                          id: 'O2ELLDgF7mA',
                          scholar: 'Dr. Craig Keener',
                          title: 'Bad Bible Proof-Texts (Part 1)',
                          description: 'What happens when the church reads the Bible out of context? Keener walks through several commonly misused passages and restores their original meaning.',
                          accent: 'text-amber-700',
                        },
                        {
                          id: 'pjAdTN03Qe8',
                          scholar: 'Dr. Craig Keener',
                          title: 'The Continuity Between the Old & New Testaments',
                          description: 'Salvation has always been by grace through faith. Keener dismantles the myth that the Old Testament is about works-based religion and the New about grace.',
                          accent: 'text-amber-700',
                        },
                        {
                          id: 'd_cVXdr8mVs',
                          scholar: 'Dr. Ben Witherington III',
                          title: 'Where Did Rapture Theology Come From?',
                          description: 'Witherington traces the surprisingly recent 19th-century roots of Rapture theology and Dispensationalism, and where it fits in Christian tradition.',
                          accent: 'text-orange-700',
                        },
                        {
                          id: 'pfXb_5ZNYfQ',
                          scholar: 'Rev. Carolyn Moore',
                          title: '9 Myths Christians Believe about Pain, Suffering & Evil',
                          description: 'Moore lays out a biblical theology of suffering, exposing common myths that leave believers confused when hardship hits.',
                          accent: 'text-rose-700',
                        },
                        {
                          id: 'DoAyhSQtKR8',
                          scholar: 'Dr. Sandra Richter',
                          title: 'Women in Ministry: The Biblical Case',
                          description: 'Richter makes a compelling scriptural case for the inclusion of women in every level of ministry — while affirming the highest view of biblical authority.',
                          accent: 'text-purple-700',
                        },
                      ].map((v) => (
                        <div
                          key={v.id}
                          className="border rounded-lg overflow-hidden bg-slate-50"
                          data-testid={`seminary-video-${v.id}`}
                        >
                          <div className="p-3 border-b bg-white">
                            <p className={`text-xs font-semibold uppercase tracking-wide ${v.accent}`}>
                              {v.scholar}
                            </p>
                            <h4 className="font-semibold text-slate-800 leading-snug">{v.title}</h4>
                          </div>
                          <div className="aspect-video w-full bg-black">
                            <iframe
                              src={`https://www.youtube.com/embed/${v.id}`}
                              title={v.title}
                              className="w-full h-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                          <div className="p-3 text-sm text-slate-600">
                            {v.description}
                          </div>
                        </div>
                      ))}

                      <p className="text-sm text-slate-500 pt-2 border-t">
                        Want more? Tap <span className="font-medium text-amber-700">View Full Playlist</span> above for the entire Seedbed 7 Minute Seminary library.
                      </p>
                    </div>
                  </div>
                </div>
                )}

                {/* Bible History Sub-section */}
                {additionalSubTab === 'history' && (
                <div id="bible-history" className="scroll-mt-20 space-y-6">
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-200">
                      <h3 className="text-xl font-bold text-indigo-800 flex items-center gap-2">
                        <Landmark className="w-5 h-5" />
                        Immersive Bible History
                      </h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Walk through Herod&apos;s Temple, tour biblical Jerusalem, and explore Second Temple archaeology —
                        all embedded below or a tap away.
                      </p>
                    </div>

                    {/* Embed 1: OpenBible.info 3D Temple Mount */}
                    <div className="p-4 border-b">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div className="min-w-0">
                          <h4 className="font-semibold text-slate-800">3D Temple Mount — Herod&apos;s Complex</h4>
                          <p className="text-xs text-slate-500">
                            Open-source WebGL model built on Leen Ritmeyer&apos;s plans + Mishnah + Josephus (OpenBible.info)
                          </p>
                        </div>
                        <a
                          href="https://www.openbible.info/labs/3d-temple-mount/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-xs text-indigo-700 hover:underline flex items-center gap-1"
                          data-testid="history-openbible-newtab"
                        >
                          Open full screen <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="aspect-video w-full rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                        <iframe
                          src="https://www.openbible.info/labs/3d-temple-mount/"
                          title="3D Temple Mount — OpenBible.info"
                          className="w-full h-full"
                          loading="lazy"
                          allow="fullscreen; xr-spatial-tracking"
                          data-testid="history-openbible-iframe"
                        />
                      </div>
                    </div>

                    {/* Embed 2: Sketchfab — Herod's Temple Mount */}
                    <div className="p-4 border-b">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div className="min-w-0">
                          <h4 className="font-semibold text-slate-800">Herod&apos;s Temple Mount — 3D Model</h4>
                          <p className="text-xs text-slate-500">
                            Rotate, zoom, and inspect a detailed 3D reconstruction (Sketchfab)
                          </p>
                        </div>
                        <a
                          href="https://sketchfab.com/3d-models/herods-temple-mount-e21c525f14a3493c96a6500d132e0e89"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-xs text-indigo-700 hover:underline flex items-center gap-1"
                          data-testid="history-sketchfab-newtab"
                        >
                          Open on Sketchfab <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                      <div className="aspect-video w-full rounded-md overflow-hidden border border-slate-200 bg-slate-50">
                        <iframe
                          src="https://sketchfab.com/models/e21c525f14a3493c96a6500d132e0e89/embed?autostart=0&ui_theme=dark"
                          title="Herod's Temple Mount — Sketchfab"
                          className="w-full h-full"
                          loading="lazy"
                          allow="autoplay; fullscreen; xr-spatial-tracking"
                          allowFullScreen
                          data-testid="history-sketchfab-iframe"
                        />
                      </div>
                    </div>

                    {/* Embed 3: YouTube — Herod's Temple Unreal Engine 5 fly-through */}
                    <div className="p-4 border-b">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div className="min-w-0">
                          <h4 className="font-semibold text-slate-800">Second Temple — Cinematic Fly-through</h4>
                          <p className="text-xs text-slate-500">
                            Unreal Engine 5 reconstruction of Herod&apos;s Temple in Jerusalem
                          </p>
                        </div>
                      </div>
                      <div className="aspect-video w-full rounded-md overflow-hidden border border-slate-200 bg-black">
                        <iframe
                          src="https://www.youtube.com/embed/2a87nR7d0hM"
                          title="Herod's Temple Mount in 3D — Unreal Engine 5"
                          className="w-full h-full"
                          loading="lazy"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          data-testid="history-yt-ue5-iframe"
                        />
                      </div>
                    </div>

                    {/* Ritmeyer channel link card (his content is on YouTube but no reliable public playlist ID) */}
                    <div className="p-4 border-b">
                      <a
                        href="https://www.youtube.com/@LeenRitmeyer/videos"
                        target="_blank"
                        rel="noreferrer noopener"
                        className="block p-4 rounded-lg border border-slate-200 bg-gradient-to-br from-indigo-50 to-white hover:from-indigo-100 hover:border-indigo-400 transition-colors"
                        data-testid="history-ritmeyer-channel-card"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs uppercase tracking-wider text-indigo-600 font-semibold mb-1">
                              YouTube Channel
                            </p>
                            <h4 className="font-semibold text-slate-800">
                              Dr. Leen Ritmeyer — Temple Mount &amp; Jerusalem Tours
                            </h4>
                            <p className="text-sm text-slate-600 mt-1">
                              The archaeologist behind the definitive Temple Mount reconstruction shares
                              tours, drawings, and lectures on his official channel.
                            </p>
                          </div>
                          <Youtube className="w-6 h-6 text-red-600 flex-shrink-0" />
                        </div>
                      </a>
                    </div>

                    {/* Native app cards — the three the user asked about */}
                    <div className="p-4 border-b">
                      <h4 className="font-semibold text-slate-800 mb-1">Native Apps &amp; VR Experiences</h4>
                      <p className="text-xs text-slate-500 mb-3">
                        Have an iPhone, iPad, or Meta Quest? These deliver the most immersive experience.
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <a
                          href="https://apps.apple.com/us/app/immersive-herods-temple/id1521739972"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
                          data-testid="history-app-herods-temple"
                        >
                          <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">iOS App</p>
                          <p className="font-semibold text-slate-800 text-sm">Immersive Herod&apos;s Temple</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Explore a fully 3D Herod&apos;s Temple on iPhone or iPad.
                          </p>
                          <span className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700">
                            App Store <ExternalLink className="w-3 h-3" />
                          </span>
                        </a>
                        <a
                          href="https://immersivehistory.com/mobile-app/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
                          data-testid="history-app-immersivehistory"
                        >
                          <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">iOS + Meta Quest</p>
                          <p className="font-semibold text-slate-800 text-sm">Immersive History — Bible Experience</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Full immersive Bible experience for phone and VR.
                          </p>
                          <span className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700">
                            Get the app <ExternalLink className="w-3 h-3" />
                          </span>
                        </a>
                        <a
                          href="https://www.ritmeyer.com/2017/04/11/virtual-jerusalem/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="block p-3 rounded-lg border border-slate-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors"
                          data-testid="history-app-virtual-jerusalem"
                        >
                          <p className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">Ritmeyer</p>
                          <p className="font-semibold text-slate-800 text-sm">Virtual Jerusalem</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Dr. Ritmeyer&apos;s virtual reconstruction and reference plans.
                          </p>
                          <span className="mt-2 inline-flex items-center gap-1 text-xs text-indigo-700">
                            Learn more <ExternalLink className="w-3 h-3" />
                          </span>
                        </a>
                      </div>
                    </div>

                    {/* Extra scholarly links */}
                    <div className="p-4">
                      <h4 className="font-semibold text-slate-800 mb-1">More Scholarly Tours</h4>
                      <p className="text-xs text-slate-500 mb-3">
                        Curated maps, articles, and virtual exhibits from trusted institutions.
                      </p>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <a
                          href="https://artsandculture.google.com/partner/the-israel-museum-jerusalem"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-sm"
                          data-testid="history-link-google-arts"
                        >
                          <span className="text-slate-700">Google Arts &amp; Culture — Israel Museum</span>
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                        </a>
                        <a
                          href="https://www.deadseascrolls.org.il/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-sm"
                          data-testid="history-link-dss"
                        >
                          <span className="text-slate-700">Digital Dead Sea Scrolls</span>
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                        </a>
                        <a
                          href="https://www.bibleodyssey.org/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-sm"
                          data-testid="history-link-bibleodyssey"
                        >
                          <span className="text-slate-700">Bible Odyssey (SBL) — Maps &amp; Articles</span>
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                        </a>
                        <a
                          href="https://www.biblicalarchaeology.org/"
                          target="_blank"
                          rel="noreferrer noopener"
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-colors text-sm"
                          data-testid="history-link-basjournal"
                        >
                          <span className="text-slate-700">Biblical Archaeology Society</span>
                          <ExternalLink className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                        </a>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-slate-500 text-center">
                    All embedded viewers are provided by their respective sites. If a viewer doesn&apos;t
                    load, tap &ldquo;Open full screen&rdquo; to view it in a new tab.
                  </p>
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* For Parents Tab — embedded Parent Resources */}
          <TabsContent value="parents" className="space-y-4" data-testid="parents-tab-content">
            <Card className="border-t-4 border-t-pink-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Home className="w-5 h-5 text-pink-500" />
                  For Parents
                  <TutorialHint videoId="kO_o_36mG3g" className="ml-1" />
                </CardTitle>
                <CardDescription>
                  Tools and guidance to help your family grow in faith together
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ParentResources embedded />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bible Book Videos dialogs (rendered once, controlled from the Interactive Guides section) */}
      <BibleBooksDialog
        open={otBooksOpen}
        onOpenChange={setOtBooksOpen}
        title="Old Testament — Book by Book"
        videos={OT_BOOK_VIDEOS}
        playlistUrl="https://www.youtube.com/playlist?list=PLH0Szn1yYNeeVFodkI9J_WEATHQCwRZ0u"
        accent="text-purple-700"
        initialSearch={booksDialogInitialSearch}
      />
      <BibleBooksDialog
        open={ntBooksOpen}
        onOpenChange={setNtBooksOpen}
        title="New Testament — Book by Book"
        videos={NT_BOOK_VIDEOS}
        playlistUrl="https://www.youtube.com/playlist?list=PLH0Szn1yYNecanpQqdixWAm3zHdhY2kPR"
        accent="text-indigo-700"
        initialSearch={booksDialogInitialSearch}
      />
    </div>
  );
};

export default Resources;
